import {createContext, type ReactNode, use} from 'react'

/**
 * Where a card menu button sits.
 *
 * - `'corner'`: the top corner at the inline end, drawn by the board
 * - `'inline'`: wherever the card renders `BoardCardMenuButton`
 */
export type BoardCardMenuPlacement = 'corner' | 'inline'

export type BoardCardMenuButtonProps = {
  /**
   * Whether the button is hidden until the pointer is over the card, the card or the button has
   * keyboard focus, or the menu is open. Devices without hover always show it.
   * @default false
   */
  revealOnHover?: boolean
  /** Class name for the element that wraps the button. */
  className?: string
}

type RenderCardMenuButton = (props: {className: string | undefined; revealOnHover: boolean}) => ReactNode

/**
 * What `BoardCardMenuButton` renders on the card around it, or `null` where the board draws the
 * button in the corner or the card has no menu.
 */
export const BoardCardMenuButtonContext = createContext<RenderCardMenuButton | null>(null)

/**
 * The card menu button, for a board with `cardMenuPlacement="inline"`. Render it inside
 * `renderCard` where the button goes.
 *
 * Renders nothing on a card with no menu, on a board that draws the button in the corner, and
 * outside a board.
 */
export function BoardCardMenuButton({revealOnHover = false, className}: BoardCardMenuButtonProps) {
  const renderButton = use(BoardCardMenuButtonContext)

  return renderButton === null ? null : renderButton({className, revealOnHover})
}
